import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getTherapistById, Therapist } from '../components/data/services';
import { Helmet } from 'react-helmet';
import {
  Mail, Linkedin, Instagram, Briefcase, Globe, Award, BookOpen, ArrowLeft, Share2,
  Calendar, MapPin, Clock, Heart, Tags, ChevronRight, Info, MessageCircle, MessageSquarePlus
} from 'lucide-react';
import { FaRupeeSign } from 'react-icons/fa';
import { Blog, getBlogById } from '../components/data/blogs';


// Function to determine background gradient based on experience
const getExperienceGradient = (experienceRange: string): string => {
  switch (experienceRange) {
    case "Less than 1 year":
      return "from-blue-400 to-blue-600";
    case "1-2 years":
      return "from-teal-400 to-teal-600";
    case "2-3 years":
      return "from-green-400 to-green-600";
    case "3-4 years":
      return "from-purple/100 to-purple";
    case "4+ years":
      return "from-amber-400 to-amber-600";
    default:
      return "from-teal-500 to-teal-700";
  }
};


const getReviewBubbleGradients = (experienceRange: string): { left: string, right: string } => {
  switch (experienceRange) {
    case "Less than 1 year":
      return {
        left: "bg-white",
        right: "bg-gradient-to-br from-blue-400 to-blue-600 text-white"
      };
    case "1-2 years":
      return {
        left: "bg-white",
        right: "bg-gradient-to-br from-teal-400 to-teal-600 text-white"
      };
    case "2-3 years":
      return {
        left: "bg-white",
        right: "bg-gradient-to-br from-green-400 to-green-600 text-white"
      };
    case "3-4 years":
      return {
        left: "bg-white",
        right: "bg-gradient-to-br from-purple-400 to-purple-600 text-white"
      };
    case "4+ years":
      return {
        left: "bg-white",
        right: "bg-gradient-to-br from-amber-400 to-amber-600 text-white"
      };
    default:
      return {
        left: "bg-white",
        right: "bg-gradient-to-br from-teal-500 to-teal-700 text-white"
      };
  }
};




// Format pricing for display
const formatHighestCharge = (pricing: Therapist['pricing']): string => {
  if (!pricing) return 'Price not available';
  return pricing.highestCharge;
};

const TherapistProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [therapist, setTherapist] = useState<Therapist | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);

  const [therapistBlogs, setTherapistBlogs] = useState<Blog[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      const foundTherapist = getTherapistById(id);
      if (foundTherapist) {
        setTherapist(foundTherapist);

        // Fetch blogs for the therapist
        if (foundTherapist.blogs && foundTherapist.blogs.length > 0) {
          const fetchedBlogs = foundTherapist.blogs.map(blogId => getBlogById(blogId)).filter(Boolean) as Blog[];
          setTherapistBlogs(fetchedBlogs);
        }

        // Pre-load image
        const img = new Image();
        img.src = foundTherapist.avatar;
      } else {
        setNotFound(true);
      }
    }
  }, [id]);

  const handleShare = () => {
    // Add a null/undefined check at the beginning
    if (!therapist) {
      console.log("Therapist data not available");
      return;
    }

    const shareUrl = window.location.href;
    // Make sure to handle the case where pricing might be undefined
    const highestCharge = therapist.pricing ? formatHighestCharge(therapist.pricing) : "varies";

    if (navigator.share) {
      // Use Web Share API if available
      navigator.share({
        title: `${therapist.name} - Greh Pravesh Therapy`,
        text: `Check out ${therapist.name}'s profile at Greh Pravesh Therapy. Fees up to ${highestCharge}.`,
        url: shareUrl,
      })
        .catch(error => console.log('Error sharing:', error));
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      // Check if clipboard API is available
      navigator.clipboard.writeText(
        `${therapist.name} - Greh Pravesh Therapy. Fees up to ${highestCharge}. ${shareUrl}`
      )
        .then(() => {
          setShowShareTooltip(true);
          setTimeout(() => setShowShareTooltip(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    } else {
      // Fallback for browsers that don't support clipboard API
      console.log('Clipboard API not available');
      // You could implement a fallback like showing a modal with the text to copy
      alert(`Copy this link: ${shareUrl}`);
    }
  };

  const handleBookAppointment = () => {
    // Save current therapist to session storage for pre-filling booking form
    if (therapist) {
      sessionStorage.setItem('selectedTherapist', JSON.stringify({
        id: id,
        name: therapist.name,
        specialization: therapist.specialization
      }));
    }
    navigate('/book-appointment');
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-16 h-16 mx-auto">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold mb-4">Therapist Not Found</h1>
          <p className="mb-6 text-gray-600">The therapist you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/our-team')}
            className="bg-teal-500 text-white px-6 py-2 rounded-lg hover:bg-teal-600 transition-all w-full flex items-center justify-center"
          >
            <ArrowLeft size={18} className="mr-2" />
            View All Therapists
          </button>
        </div>
      </div>
    );
  }

  if (!therapist) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse space-y-8 p-6 max-w-4xl w-full">
          <div className="flex items-center space-x-4">
            <div className="rounded-full bg-slate-200 h-24 w-24"></div>
            <div className="flex-1 space-y-4">
              <div className="h-6 bg-slate-200 rounded w-1/2"></div>
              <div className="h-4 bg-slate-200 rounded w-1/4"></div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-32 bg-slate-200 rounded col-span-1"></div>
            <div className="h-32 bg-slate-200 rounded col-span-1"></div>
            <div className="h-32 bg-slate-200 rounded col-span-1"></div>
          </div>
          <div className="h-48 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  // Get gradient based on experience range
  const experienceGradient = getExperienceGradient(therapist.experienceRange);

  return (
    <>
      <Helmet>
        <title>{therapist.name} - Therapist | Greh Pravesh Therapy</title>
        <meta name="description" content={`${therapist.name} - Expert in ${therapist.specialization.join(', ')}. Specialized approach for lasting results. Fees up to ${formatHighestCharge(therapist.pricing)}. Call 8591049845 to book now.`} />
         <meta property="og:title" content={`${therapist.name} - Therapist | Greh Pravesh Therapy`} />
        <meta property="og:description" content={`${therapist.name} is a therapist specializing in ${therapist.specialization.join(', ')}. Fees up to ${formatHighestCharge(therapist.pricing)}. Learn more about their approach and book a session.`} />
        <meta property="og:image" content={therapist.avatar} />
        <meta property="og:type" content="profile" />
      </Helmet>

      <main className="bg-gray-50 min-h-screen pb-12 mt-28">
        {/* Header with navigation */}

        <div className="container mx-auto py-6 px-2 md:px-4">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Header with Avatar */}
            <div className={`bg-gradient-to-r ${experienceGradient} p-8 relative`}>
              <div className="flex flex-col md:flex-row items-center">
                <div className="relative mb-6 md:mb-0 md:mr-8">
                  <img
                    src={therapist.avatar}
                    alt={therapist.name}
                    className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
                    loading="lazy"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-white rounded-full shadow px-3 py-1 text-sm font-medium text-gray-700">
                    {therapist.experience} yrs experience
                  </div>
                </div>
                <div className="text-center md:text-left">
                  <h1 className="text-4xl font-bold text-white mb-2 leading-tight">{therapist.name}</h1>
                  <p className="text-white/90 mb-4">{therapist.pronouns}</p>
                  {/* <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    {therapist.specialization.slice(0, 3).map((spec, index) => (
                      <span
                        key={spec}
                        className="bg-white/20 text-white text-sm px-3 py-1 rounded-full backdrop-blur-sm flex items-center"
                      >
                        {index === 0 && <Check size={14} className="mr-1" />}
                        {spec}
                      </span>
                    ))}
                    {therapist.specialization.length > 3 && (
                      <span className="bg-white/10 text-white text-sm px-3 py-1 rounded-full backdrop-blur-sm">
                        +{therapist.specialization.length - 3} more
                      </span>
                    )}
                  </div> */}
                </div>
              </div>

              {/* Share button positioned in top-right corner */}
              <div className="absolute top-4 right-4">
                <button
                  onClick={handleShare}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full p-2 transition-colors flex items-center"
                  aria-label="Share profile"
                >
                  <Share2 size={18} />
                  <span className="ml-2 hidden md:inline">Share</span>
                </button>

                {showShareTooltip && (
                  <div className="absolute right-0 top-full mt-2 bg-black text-white text-sm py-1 px-3 rounded-md">
                    Copied to clipboard!
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Quick Actions Bar
          <div className="border-b border-gray-100 sticky top-16 bg-white z-10 shadow-sm">
            <div className="container mx-auto flex items-center justify-between overflow-x-auto">
              <div className="flex items-center">
                <a href="#availability" className="px-4 py-3 text-gray-600 hover:text-teal-600 hover:border-b-2 hover:border-teal-500 transition-colors whitespace-nowrap">Availability</a>
                <a href="#qualifications" className="px-4 py-3 text-gray-600 hover:text-teal-600 hover:border-b-2 hover:border-teal-500 transition-colors whitespace-nowrap">Qualifications</a>
                <a href="#concerns" className="px-4 py-3 text-gray-600 hover:text-teal-600 hover:border-b-2 hover:border-teal-500 transition-colors whitespace-nowrap">Areas of Concern</a>
                <a href="#pricing" className="px-4 py-3 text-gray-600 hover:text-teal-600 hover:border-b-2 hover:border-teal-500 transition-colors whitespace-nowrap">Pricing</a>
                <a href="#about" className="px-4 py-3 text-gray-600 hover:text-teal-600 hover:border-b-2 hover:border-teal-500 transition-colors whitespace-nowrap">About</a>
              </div>
              <div className="ml-auto">
                <button
                  onClick={handleBookAppointment}
                  className="bg-teal-50 text-teal-600 hover:bg-teal-100 px-4 py-3 flex items-center font-medium transition-colors"
                >
                  <Calendar size={16} className="mr-2" />
                  Book Now
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-gray-100 border-b bg-gray-50">
            <div className="p-4 flex flex-col items-center text-center hover:bg-gray-100 transition-colors cursor-pointer">
              <Briefcase className="text-blue-500 mb-2" size={20} />
              <p className="text-sm text-gray-500">Experience</p>
              <p className="font-medium">
                {therapist.experience} {therapist.experience === 1 ? 'Year' : 'Years'}
              </p>
            </div>
            <div className="p-4 flex flex-col items-center text-center hover:bg-gray-100 transition-colors cursor-pointer">
              <FaRupeeSign className="text-green-500 mb-2" size={20} />
              <p className="text-sm text-gray-500">Fees up to</p>
              <p className="font-medium">{formatHighestCharge(therapist.pricing)}</p>
            </div>
            <div className="p-4 flex flex-col items-center text-center hover:bg-gray-100 transition-colors cursor-pointer">
              <Globe className="text-purple-500 mb-2" size={20} />
              <p className="text-sm text-gray-500">Languages</p>
              <p className="font-medium">{therapist.languages.length} Languages</p>
            </div>
            <div className="p-4 flex flex-col items-center text-center hover:bg-gray-100 transition-colors cursor-pointer">
              <MapPin className="text-amber-500 mb-2" size={20} />
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium">{therapist.contact.location || "Online"}</p>
            </div>
          </div>

          {/* Detailed Information */}
          <div className="p-6 md:p-8 grid md:grid-cols-3 gap-6 md:gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              <div id="availability" className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Calendar className="mr-2 text-blue-600" size={20} />
                    Availability
                  </h2>
                </div>
                <div className="p-4">
                  {therapist.availability?.map((slot, index) => (
                    <div key={index} className="mb-3 last:mb-0">
                      <div className="flex items-start">
                        <Clock size={16} className="text-gray-500 mt-1 mr-2 flex-shrink-0" />
                        <div>
                          <p className="font-medium">{slot.days}</p>
                          <p className="text-gray-600 text-sm">{slot.hours}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Globe className="mr-2 text-green-600" size={20} />
                    Languages
                  </h2>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {therapist.languages.map(lang => (
                      <span
                        key={lang}
                        className="bg-green-50 text-green-700 text-sm px-3 py-1 rounded-full"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Middle Column */}
            <div className="space-y-6">
              <div id="qualifications" className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Award className="mr-2 text-purple-600" size={20} />
                    Qualifications
                  </h2>
                </div>
                <div className="p-4">
                  {therapist.qualifications.map((qual, index) => (
                    <div key={index} className="mb-3 last:mb-0 p-3 bg-gray-50 rounded-lg">
                      <p className="font-medium">{qual.degree}</p>
                      {qual.institution && <p className="text-gray-600 text-sm">{qual.institution}</p>}
                    </div>
                  ))}
                </div>
              </div>

              <div id="concerns" className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Heart className="mr-2 text-red-600" size={20} />
                    Areas of Concern
                  </h2>
                </div>
                <div className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {therapist.concerns?.map(concern => (
                      <span
                        key={concern}
                        className="bg-red-50 text-red-700 text-sm px-3 py-1 rounded-full"
                      >
                        {concern}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <div id="pricing" className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FaRupeeSign className="mr-2 text-amber-600" size={20} />
                    Pricing
                  </h2>
                </div>
                <div className="p-4">
                  {therapist.pricing?.standardFees?.map((fee, idx) => (
                    <div key={idx} className="mb-3 last:mb-0 p-3 bg-gray-50 rounded-lg">
                      {fee.individual && (
                        <div className="mb-2">
                          <div className="flex justify-between items-center">
                            <p className="font-medium">Individual Session</p>
                            <span className="text-teal-600 font-semibold">
                              {fee.individual.amount} {fee.individual.currency}
                            </span>
                          </div>
                          {fee.individual.duration && <p className="text-sm text-gray-500">{fee.individual.duration}</p>}
                          {fee.individual.location && <p className="text-sm text-gray-500">{fee.individual.location}</p>}
                        </div>
                      )}
                      {fee.couples && (
                        <div className='mb-2'>
                          <div className="flex justify-between items-center">
                            <p className="font-medium">Couples Session</p>
                            <span className="text-teal-600 font-semibold">
                              {fee.couples.amount} {fee.couples.currency}
                            </span>
                          </div>
                          {fee.couples.duration && <p className="text-sm text-gray-500">{fee.couples.duration}</p>}
                          {fee.couples.location && <p className="text-sm text-gray-500">{fee.couples.location}</p>}
                        </div>
                      )}
                      {fee.family && (
                        <div className='mb-2'>
                          <div className="flex justify-between items-center">
                            <p className="font-medium">Family Session</p>
                            <span className="text-teal-600 font-semibold">
                              {fee.family.amount} {fee.family.currency}
                            </span>
                          </div>
                          {fee.family.duration && <p className="text-sm text-gray-500">{fee.family.duration}</p>}
                          {fee.family.location && <p className="text-sm text-gray-500">{fee.family.location}</p>}
                        </div>
                      )}
                    </div>
                  ))}

                  {therapist.pricing?.slidingScale && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center mb-2">
                        <Info size={16} className="text-blue-600 mr-2" />
                        <p className="font-medium text-blue-800">Sliding Scale Available</p>
                      </div>
                      {therapist.pricing.slidingScale.range && (
                        <p className="text-sm text-blue-700">
                          Range: {therapist.pricing.slidingScale.range.min} - {therapist.pricing.slidingScale.range.max}
                        </p>
                      )}
                      {therapist.pricing.slidingScale.availableFor && (
                        <p className="text-sm text-blue-700 mt-1">
                          Available for: {therapist.pricing.slidingScale.availableFor.join(', ')}
                        </p>
                      )}
                      {therapist.pricing.slidingScale.slots && (
                        <p className="text-sm text-blue-700 mt-1">
                          Limited slots: {therapist.pricing.slidingScale.slots}
                        </p>
                      )}
                    </div>
                  )}

                  {therapist.probono && (
                    <div className="mt-3 p-3 bg-teal-50 rounded-lg border border-teal-100">
                      <div className="flex items-center">
                        <Heart size={16} className="text-teal-600 mr-2" />
                        <p className="font-medium text-teal-800">Pro Bono Sessions Available</p>
                      </div>
                      <p className="text-sm text-teal-700 mt-1">Please inquire for eligibility</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Mail className="mr-2 text-teal-600" size={20} />
                    Contact
                  </h2>
                </div>
                <div className="p-4">
                  <div className="space-y-3">
                    <a
                      href={`mailto:${therapist.contact.email}`}
                      className="flex items-center p-2 hover:bg-teal-50 rounded-lg transition-colors group"
                    >
                      <Mail size={16} className="mr-3 text-teal-500" />
                      <span className="text-gray-700 group-hover:text-teal-700">{therapist.contact.email}</span>
                    </a>
                    {therapist.contact.phone && (
                      <a
                        href={`tel:${therapist.contact.phone}`}
                        className="flex items-center p-2 hover:bg-purple-50 rounded-lg transition-colors group"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-3 text-purple-500">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        <span className="text-gray-700 group-hover:text-purple-700">{therapist.contact.phone}</span>
                      </a>
                    )}

                    <div className="flex space-x-3 mt-4 p-2">
                      {therapist.contact.linkedin && (
                        <a
                          href={therapist.contact.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-blue-50 hover:bg-blue-100 text-blue-600 p-2 rounded-lg transition-colors flex items-center"
                          aria-label="LinkedIn Profile"
                        >
                          <Linkedin size={18} className="mr-2" />
                          <span className="text-sm font-medium">LinkedIn</span>
                        </a>
                      )}

                      {therapist.contact.instagram && (
                        <a
                          href={therapist.contact.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-pink-50 hover:bg-pink-100 text-pink-600 p-2 rounded-lg transition-colors flex items-center"
                          aria-label="Instagram Profile"
                        >
                          <Instagram size={18} className="mr-2" />
                          <span className="text-sm font-medium">Instagram</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Full Width Bio */}
            <div id="about" className="md:col-span-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
              <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <BookOpen className="mr-2 text-blue-600" size={20} />
                  About {therapist.name}
                </h2>
              </div>
              <div className="p-6">
                <div className="prose max-w-none text-gray-700">
                  <p className="leading-relaxed">{therapist.bio}</p>
                </div>
              </div>
            </div>

            {therapistBlogs.length > 0 && (
              <div className="md:col-span-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100 flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <BookOpen className="mr-2 text-purple-600" size={20} />
                    Blog Posts by {therapist.name}
                  </h2>
                  {therapistBlogs.length > 3 && (
                    <Link
                      to={`/blogs/author/${therapist.id}`}
                      className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center"
                    >
                      View All <ChevronRight size={16} />
                    </Link>
                  )}
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {therapistBlogs.slice(0, 3).map(blog => (
                      <Link
                        key={blog.id}
                        to={`/blog/${blog.slug}`}
                        className="group"
                      >
                        <div className="border border-gray-100 rounded-lg overflow-hidden h-full hover:shadow-md transition-shadow flex flex-col">
                          <div className="p-4 flex-grow">
                            <h3 className="font-semibold text-lg mb-2 group-hover:text-teal-600 transition-colors line-clamp-2">
                              {blog.title}
                            </h3>
                            <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                              {blog.excerpt}
                            </p>
                            <div className="flex flex-wrap gap-2 mt-auto">
                              {blog.tags.slice(0, 3).map(tag => (
                                <span
                                  key={tag}
                                  className="bg-purple-50 text-purple-700 text-xs px-2 py-1 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          {/* <div className="border-t border-gray-100 p-3 bg-gray-50 flex justify-between items-center">
                            <span className="text-xs text-gray-500">By {blog?.author.name && blog?.author.name}</span>
                            <span className="text-teal-600 text-sm font-medium group-hover:underline flex items-center">
                              Read <ChevronRight size={14} className="ml-1" />
                            </span>
                          </div> */}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {/* Expertise Section */}
            {therapist.expertise && therapist.expertise.length > 0 && (
              <div className="md:col-span-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow">
                <div className="bg-gray-50 p-4 rounded-t-xl border-b border-gray-100">
                  <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <Tags className="mr-2 text-indigo-600" size={20} />
                    Expertise
                  </h2>
                </div>
                <div className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {therapist.expertise.map(item => (
                      <span
                        key={item}
                        className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {therapist.reviews && therapist.reviews.length > 0 && (
              <div className="md:col-span-3 bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow transition-shadow ">
                <div className={`bg-gradient-to-r ${getExperienceGradient(therapist.experienceRange)} p-4 rounded-t-xl border-b border-gray-100`}>
                  <h2 className="text-xl font-semibold text-white flex items-center">
                    <MessageCircle className="mr-2" size={20} />
                    Client Reviews
                    <span className="ml-2 bg-white/20 text-white text-xs font-medium px-2.5 py-0.5 rounded-full backdrop-blur-sm">
                      {therapist.reviews.length}
                    </span>
                  </h2>
                </div>
                {/* WhatsApp style chat background with subtle pattern */}

                <div className="p-4 sm:p-2 md:p-6 relative whatsapp-bg"
                >
                  {/* Chat container */}
                  {/* Chat container */}
                  <div className="flex flex-col space-y-6 max-h-[32rem] overflow-y-auto no-scrollbar px-2 py-3">
                    {therapist.reviews.map((review, index) => {
                      const bubbleGradients = getReviewBubbleGradients(therapist.experienceRange);
                      return (
                        <div key={index} className={`flex flex-col w-full sm:max-w-[90%] md:max-w-[80%] ${index % 2 === 0 ? 'self-start' : 'self-end'}`}>
                          <div className={`relative p-4 rounded-xl shadow-sm ${index % 2 === 0
                            ? "bg-white rounded-tl-none"
                            : `${bubbleGradients.right} rounded-tr-none`
                            }`}>
                            <p className={`text-sm sm:text-base md:text-base leading-relaxed ${index % 2 === 0 ? 'text-gray-700' : 'text-white/95'}`}>
                              {review.review}
                            </p>
                            {/* Triangular chat bubble tail */}
                            <div className={`absolute ${index % 2 === 0
                              ? "left-0 -translate-x-3 top-0 border-r-[12px] border-r-white"
                              : `right-0 translate-x-3 top-0 border-l-[12px] ${bubbleGradients.right.includes('from-blue') ? 'border-l-blue-500' :
                                bubbleGradients.right.includes('from-teal') ? 'border-l-teal-500' :
                                  bubbleGradients.right.includes('from-green') ? 'border-l-green-500' :
                                    bubbleGradients.right.includes('from-purple') ? 'border-l-purple-500' :
                                      'border-l-amber-500'
                              }`
                              } border-y-[12px] border-y-transparent h-0 w-0`}>
                            </div>
                          </div>
                          <div className={`mt-2 ${index % 2 === 0 ? 'self-start ml-3' : 'self-end mr-3'}`}>
                            <span className={`inline-block px-3 py-1 rounded-full font-medium text-base sm:text-lg ${index % 2 === 0
                              ? 'bg-gray-100 text-gray-800'
                              : therapist.experienceRange === "Less than 1 year" ? 'bg-blue-100 text-blue-700' :
                                therapist.experienceRange === "1-2 years" ? 'bg-teal-100 text-teal-700' :
                                  therapist.experienceRange === "2-3 years" ? 'bg-green-100 text-green-700' :
                                    therapist.experienceRange === "3-4 years" ? 'bg-purple-100 text-purple-700' :
                                      'bg-amber-100 text-amber-700'
                              }`}>
                              {!review.client ? "Anon" : review.client}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add review CTA */}
                  <div className="mt-8 text-center">
                    <div className="mb-2 flex items-center justify-center text-sm text-gray-600">
                      <span>All reviews verified from</span>
                      {/* Inline SVG Google logo */}
                      <svg
                        viewBox="0 0 272 92"
                        className="h-8 mx-1 w-20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#EA4335" />
                        <path d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z" fill="#FBBC05" />
                        <path d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z" fill="#4285F4" />
                        <path d="M225 3v65h-9.5V3h9.5z" fill="#34A853" />
                        <path d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z" fill="#EA4335" />
                        <path d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z" fill="#4285F4" />
                      </svg>
                      <span>Reviews</span>
                    </div>
                    <a
                      href='https://www.google.com/search?q=ghrepravesh+therapy&oq=ghrepravesh+therapy&gs_lcrp=EgZjaHJvbWUyBggAEEUYOdIBCDQwNzFqMGo0qAIAsAIB&sourceid=chrome&ie=UTF-8&lqi=ChNnaHJlcHJhdmVzaCB0aGVyYXB5SNDWmufsu4CACFobEAAQARgBIhNnaHJlcHJhdmVzaCB0aGVyYXB5kgEMcHN5Y2hvbG9naXN0#lkt=LocalPoiReviews&rlimm=1903867682227248666&lrd=0x390d033085c4a331:0x1a6be591e2805e1a,3,,,,'
                      className={`inline-flex items-center px-5 py-2.5 bg-gradient-to-r ${getExperienceGradient(therapist.experienceRange)} text-white rounded-lg hover:shadow-md transition-all text-sm font-medium`}>
                      <MessageSquarePlus size={18} className="mr-2" />
                      Share Your Experience
                    </a>
                  </div>
                </div>
              </div>
            )}


            {/* Sticky Bottom Action Bar for Mobile */}
            {therapist && (
              <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-3 md:hidden">
                <div className="flex justify-between gap-3">
                  <button
                    onClick={handleShare}
                    className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center"
                  >
                    <Share2 size={18} className="mr-2" />
                    Share
                  </button>
                  <button
                    onClick={handleBookAppointment}
                    className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-3 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-colors flex items-center justify-center font-medium"
                  >
                    <Calendar size={18} className="mr-2" />
                    Book Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main >
    </>
  )
}

export default TherapistProfile;


